import React from 'react';
import PropTypes from 'prop-types';
import { Badge, Spacing, Text } from '@reservamos/elements';
import FALLBACK_DOT_COLOR from 'constants/seatColors';
import LocalizedCurrency from 'components/LocalizedCurrency';

/**
 * SeatPricingDetailRow component.
 * Render a seat pricing row with two layout variants:
 *
 * - "stacked" (default): two rows — seat label + deck badge, then dot + label + price.
 *   Used by zone-based pricing tenants.
 * - "inline": single row — dot + label + seat label, with deck badge on the right and no price.
 *   Used by Privileged Seat (IAMSA), where the price is already in the passenger header.
 *
 * @param {object} props - t, seatLabel, showSeatFloor, seatFloor, indicatorColor, label, price, variant.
 * @param {Function} props.t - i18n translation function (deck badge copy).
 * @param {string} props.seatLabel - Resolved seat text ("Asiento 24" or no-assigned-seat copy).
 * @param {boolean} props.showSeatFloor - Whether to render the deck badge.
 * @param {string|number} props.seatFloor - Deck label when shown.
 * @param {string} props.indicatorColor - Resolved CSS color for the dot; falls back to gray.
 * @param {string} props.label - Resolved label shown next to the dot (zone or passenger category).
 * @param {number} props.price - Row price (only rendered in "stacked" variant).
 * @param {"stacked"|"inline"} props.variant - Layout variant.
 * @returns {JSX.Element} SeatPricingDetailRow component.
 */
const SeatPricingDetailRow = ({
  t,
  seatLabel,
  showSeatFloor,
  seatFloor,
  indicatorColor,
  label,
  price,
  variant,
}) => {
  const dot = (
    <span
      data-testid="seat-pricing-dot"
      style={{
        width: 8,
        height: 8,
        minWidth: 8,
        borderRadius: '50%',
        backgroundColor: indicatorColor || FALLBACK_DOT_COLOR,
      }}
    />
  );

  const deckBadge = showSeatFloor ? (
    <Badge roundCorners type="gray">
      <Text elementType="span" size="S" weight="semibold">
        {t('general:deck')}
        <Text elementType="span" size="S" weight="bold">
          {' '}
          {seatFloor}
        </Text>
      </Text>
    </Badge>
  ) : null;

  if (variant === 'inline') {
    return (
      <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
        <Spacing size="XS" alignItems="center">
          {dot}
          <Text size="S" color="grayLight" weight="regular">
            {label}
          </Text>
          <Text size="S" color="grayLight" weight="regular">
            {seatLabel}
          </Text>
        </Spacing>
        {deckBadge}
      </Spacing>
    );
  }

  return (
    <Spacing size="XS" vertical fullWidth>
      <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
        <Text size="S" color="grayLight" weight="regular">
          {seatLabel}
        </Text>
        {deckBadge}
      </Spacing>

      <Spacing size="S" alignItems="center" justifyContent="space-between" fullWidth>
        <Spacing size="XS" alignItems="center">
          {dot}
          <Text size="S" color="grayLight" weight="regular">
            {label}
          </Text>
        </Spacing>
        <LocalizedCurrency
          price={price}
          currencyStyle="currency"
          size="S"
          color="grayLight"
          weight="regular"
        />
      </Spacing>
    </Spacing>
  );
};

SeatPricingDetailRow.propTypes = {
  t: PropTypes.func.isRequired,
  seatLabel: PropTypes.string.isRequired,
  showSeatFloor: PropTypes.bool,
  seatFloor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  indicatorColor: PropTypes.string,
  label: PropTypes.string,
  price: PropTypes.number,
  variant: PropTypes.oneOf(['stacked', 'inline']),
};

SeatPricingDetailRow.defaultProps = {
  showSeatFloor: false,
  seatFloor: undefined,
  indicatorColor: undefined,
  label: undefined,
  price: undefined,
  variant: 'stacked',
};

export default SeatPricingDetailRow;
